import axios from 'axios'

/**
 * Single home for HTTP-error-shape knowledge in the webapp.
 *
 * Screens ask named questions — `isLeaveBlockedByBalances(err)`, `isConflict(err)`
 * — instead of reaching into `error.response.status` and hard-coding magic
 * numbers. The axios dependency and every HTTP status code the UI reacts to live
 * here and nowhere else, so a transport change touches one file and each status
 * code is named exactly once.
 */

const statusOf = (error: unknown): number | undefined =>
	axios.isAxiosError(error) ? error.response?.status : undefined

/** 400 — the request failed server-side validation. */
export const isBadRequest = (error: unknown): boolean => statusOf(error) === 400

/** 404 — the looked-up resource does not exist. */
export const isNotFound = (error: unknown): boolean => statusOf(error) === 404

/** 409 — the change conflicts with current server state. */
export const isConflict = (error: unknown): boolean => statusOf(error) === 409

/** 413 — the upload exceeds the request size limit. */
export const isPayloadTooLarge = (error: unknown): boolean => statusOf(error) === 413

/** 429 — the caller is rate limited. */
export const isRateLimited = (error: unknown): boolean => statusOf(error) === 429

/**
 * 441 — Orbit's non-standard "Forbidden - active expenses" code: leaving a trip
 * is blocked because the member is still involved in expenses or pay-ups. Named
 * here so the meaning of 441 lives in exactly one place.
 */
export const isLeaveBlockedByBalances = (error: unknown): boolean => statusOf(error) === 441

/** 5xx — the server failed to handle an otherwise-valid request. */
export const isServerError = (error: unknown): boolean => {
	const status = statusOf(error)
	return status !== undefined && status >= 500
}

/** The HTTP response carried by an API error, in transport-agnostic shape. */
export interface ApiErrorResponse {
	status: number
	data: unknown
	headers: Record<string, unknown>
}

/**
 * The response carried by an API error, or undefined for non-HTTP failures
 * (network error, or a thrown non-axios value). Lets callers read the response
 * headers/body without importing axios themselves.
 */
export const getApiErrorResponse = (error: unknown): ApiErrorResponse | undefined => {
	if (axios.isAxiosError(error) && error.response) {
		return {
			status: error.response.status,
			data: error.response.data,
			headers: (error.response.headers ?? {}) as Record<string, unknown>,
		}
	}
	return undefined
}

/**
 * Maps any thrown value to a user-facing message. Distinguishes network failures
 * and 5xx responses from server-supplied messages, falling back to
 * `defaultMessage` when the error shape is unknown.
 */
export const getErrorMessage = (error: unknown, defaultMessage: string): string => {
	if (axios.isAxiosError(error)) {
		// Network error (no internet, connection refused, etc.)
		if (!error.response) {
			return 'Network error. Please check your internet connection and try again.'
		}
		// Server error (error response from server)
		const status = error.response?.status
		const message = error.response?.data?.message || error.message

		if (status && status >= 500) {
			return 'Server error. Please try again later.'
		}

		return message || defaultMessage
	}

	// Generic error
	return defaultMessage
}
